import './ServiceOrderFilter.scss';

import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useForm } from '@refinedev/antd';
import { Button, Form, Popover } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import FullscreenDrawer from '@/components/general/Drawers/FullscreenDrawer';
import { useUiContext } from '@/contexts/UiContext';
import {
  ServiceOrderStatusString,
  ServiceOrderTypeString,
} from '@/model/ServiceOrder/ServiceOrder';
import { Ticket } from '@/model/Ticket/Ticket';

import useServiceOrderFilter from './useServiceOrderFilter';

interface ServiceOrderFilterProps {
  filters: any[];
  setFilters: Function;
  setCurrent: Function;
}

const ServiceOrderFilter = (props: ServiceOrderFilterProps) => {
  const { t } = useTranslation();
  const { isMobile } = useUiContext();
  const { filters, setFilters, setCurrent } = props;
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const { formProps } = useForm<Ticket>({ warnWhenUnsavedChanges: false });
  const { form } = formProps;
  const { ticketSelectProps, companySelectProps, technicianSelectProps } = useServiceOrderFilter(
    formProps,
    filters,
  );
  const applyFilters = useCallback(
    values => {
      const filters: any[] = [];

      // Ticket
      if (values.ticket) {
        filters.push({
          field: 'tickets',
          operator: 'eq',
          value: values.tickets,
        });
      }

      // Tecnico
      if (values.technician) {
        filters.push({
          field: 'technician',
          operator: 'eq',
          value: values.technician,
        });
      }

      // Empresa
      if (values.company) {
        filters.push({
          field: 'company',
          operator: 'eq',
          value: values.company,
        });
      }

      // tipo
      if (values.type != undefined) {
        filters.push({
          field: 'type',
          operator: 'eq',
          value: values.type,
        });
      }

      // estado
      if (values.status != undefined) {
        filters.push({
          field: 'status',
          operator: 'eq',
          value: values.status,
        });
      }

      // estado de stock
      if (values.stock_technician_reviewed != undefined) {
        let stockValue = values.stock_technician_reviewed;

        if (stockValue === 'true') stockValue = true;
        else if (stockValue === 'false') stockValue = false;
        filters.push({
          field: 'stock_technician_reviewed',
          operator: 'eq',
          value: stockValue,
        });
      }

      setFilters(filters, 'replace');
      setCurrent(1);
      setFilterPopoverOpen(false);
    },
    [setFilters, setFilterPopoverOpen, setCurrent],
  );
  const handleClearFilters = useCallback(() => {
    form?.resetFields();
    setFilters([], 'replace');
    setCurrent(1);
    setFilterPopoverOpen(false);
  }, [form, setCurrent, setFilters]);
  const filterContent = (
    <Form
      {...formProps}
      labelAlign="left"
      style={{ marginBottom: 8 }}
      onFinish={applyFilters}
      className="ServiceOrderFilterPopover">
      <div className="ServiceOrderFilterPopoverRows">
        <Form.Item label={t('Ticket')} name={'tickets'} className="ServiceOrderFilterRow">
          <AntdSelect {...ticketSelectProps} allowClear showSearch />
        </Form.Item>
        <Form.Item label={t('Técnico')} name={'technician'} className="ServiceOrderFilterRow">
          <AntdSelect {...technicianSelectProps} allowClear showSearch />
        </Form.Item>
        <Form.Item label={t('Empresa')} name={'company'} className="ServiceOrderFilterRow">
          {/* <CompanySelector showFullName {...companySelectProps} allowClear showSearch /> */}
          <AntdSelect {...companySelectProps} allowClear showSearch />
        </Form.Item>
        <Form.Item label={t('Tipo')} name={'type'} className="ServiceOrderFilterRow">
          <AntdSelect
            options={Object.keys(ServiceOrderTypeString).map(v => ({
              label: ServiceOrderTypeString[parseInt(v)],
              value: parseInt(v),
            }))}
            allowClear
          />
        </Form.Item>
        <Form.Item label={t('Estado')} name={'status'} className="ServiceOrderFilterRow">
          <AntdSelect
            options={Object.keys(ServiceOrderStatusString).map(v => ({
              label: ServiceOrderStatusString[parseInt(v)],
              value: parseInt(v),
            }))}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label={t('Estado de stock')}
          name={'stock_technician_reviewed'}
          className="ServiceOrderFilterRow">
          <AntdSelect
            options={[
              { label: t('Revisado'), value: 'true' },
              { label: t('Pendiente de revisión'), value: 'false' },
            ]}
            allowClear
          />
        </Form.Item>
      </div>

      <div className="ServiceOrderFilterActions">
        <Button type="default" style={{ marginLeft: 8 }} onClick={handleClearFilters}>
          {t('Limpiar')}
        </Button>
        <Button htmlType="submit" type="primary">
          {t('Buscar')}
        </Button>
      </div>
    </Form>
  );

  return (
    <div className="ServiceOrderFilter">
      {isMobile ? (
        <>
          <Button
            className={filters.length > 0 ? 'ServiceOrderFilterApplied' : ''}
            onClick={() => setFilterPopoverOpen(true)}
            icon={<FilterOutlined />}
          />
          <FullscreenDrawer
            disablePush
            title={t('Filtros')}
            open={filterPopoverOpen}
            setOpen={setFilterPopoverOpen}>
            {filterContent}
          </FullscreenDrawer>
        </>
      ) : (
        <Popover
          rootClassName="ServiceOrderFilterPopover"
          content={filterContent}
          title={t('Filtros')}
          trigger="click"
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          placement="bottomRight">
          <Button
            className={filters.length > 0 ? 'ServiceOrderFilterApplied' : ''}
            icon={filters.length > 0 ? <FilterFilled /> : <FilterOutlined />}>
            {t('Filtrar')}
          </Button>
        </Popover>
      )}
    </div>
  );
};

export default ServiceOrderFilter;
